import React, { useEffect, useState } from "react";
import { Clock, Spinner, PlusCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function daysLeft(iso) {
  if (!iso) return 0;
  try {
    const ends = new Date(iso);
    const diff = ends.getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (24 * 3600 * 1000)));
  } catch { return 0; }
}

export default function TrialExtensionCard() {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trials");
      setTrials(data.trials || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const extend = async (userId, days) => {
    setBusyId(userId);
    try {
      const { data } = await api.post("/admin/trial/extend", { user_id: userId, days });
      toast.success(`+${data.days_added} days added`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2" data-testid="trial-ext-card">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={16} weight="duotone" className="text-orange-500" />
        <div className="font-display font-medium text-zinc-100">Active Trials</div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Grant an extension when a power user asks for more time. Each click adds days to their trial and clears any prior expiry emails.
      </p>

      {loading ? (
        <div className="text-center text-zinc-500 py-4"><Spinner size={16} className="animate-spin mx-auto" /></div>
      ) : trials.length === 0 ? (
        <div data-testid="trial-ext-empty" className="text-sm text-zinc-500 py-4 text-center border border-zinc-800/60 rounded-sm bg-zinc-950/40">
          No active trials right now.
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/60 border border-zinc-800/60 rounded-sm bg-zinc-950/40">
          {trials.map((t) => {
            const dl = daysLeft(t.trial_ends_at);
            return (
              <div key={t.user_id} data-testid={`trial-row-${t.email}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{t.name}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{t.email} · {dl}d left</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === t.user_id}
                    onClick={() => extend(t.user_id, 7)}
                    data-testid={`trial-ext-7-${t.email}`}
                    className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10 rounded-sm h-7 text-xs"
                  >
                    {busyId === t.user_id ? <Spinner size={12} className="animate-spin" /> : <><PlusCircle size={12} weight="fill" className="mr-1" /> 7d</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === t.user_id}
                    onClick={() => extend(t.user_id, 14)}
                    data-testid={`trial-ext-14-${t.email}`}
                    className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10 rounded-sm h-7 text-xs"
                  >
                    {busyId === t.user_id ? <Spinner size={12} className="animate-spin" /> : <><PlusCircle size={12} weight="fill" className="mr-1" /> 14d</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
